/**
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ K2 — SEEDS / ONBOARDING · dashboard welcome card                         │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * Shown ONLY to a pristine guest (no live lists or tasks and no previously
 * persisted decision) and offers to seed a sample list that teaches the domain
 * (complete, Important, Planned, My Day, details). The seed is intentionally
 * OPT-IN: seeding at boot would break the "clean guest = no data" contract of
 * the guest→account migration flow (CC-27/CC-28).
 *
 * The decision ("seeded" | "dismissed") is persisted per account in localStorage
 * via the `@listto/shared` kit (onboarding/model), so it survives reloads
 * but does NOT travel between devices (correct: it is a local startup
 * preference, not user-data).
 */
import { msg } from "@lingui/core/macro";
import { createEffect, createMemo, createSignal, Show } from "solid-js";
import { Sparkles } from "@fleet/icons";
import {
  applyWelcomeSeed,
  buildWelcomeSeedSpec,
  createListMutation,
  createTodoMutation,
  getLiveListCountForUserQuery,
  getLiveTodoCountForUserQuery,
  type OnboardingMarker,
  queryKeys,
  readOnboardingMarker,
  shouldShowOnboarding,
  VISITOR_ID,
  writeOnboardingMarker,
} from "@listto/shared";
import { createPowerSyncQuery } from "@listto/shared/solid";
import { Button } from "@/components/ui/button";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { useNavigate } from "@tanstack/solid-router";

/** Tolerant localStorage: in contexts without DOM it degrades to "no marker". */
function markerStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

function readMarker(userId: string): OnboardingMarker | null {
  const storage = markerStorage();
  return storage ? readOnboardingMarker(storage, userId) : null;
}

export function OnboardingCard() {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { userId } = session;
  const navigate = useNavigate();

  const isGuest = () => userId() === VISITOR_ID;

  // Reactive marker for the in-app account switch (tab pin).
  const [marker, setMarker] = createSignal<OnboardingMarker | null>(readMarker(userId()));
  createEffect(() => {
    setMarker(readMarker(userId()));
  });

  const [seeding, setSeeding] = createSignal(false);

  // Live emptiness (live layer: trash excluded). It is queried only for the
  // guest — the card never applies to real accounts.
  const todoCount = createPowerSyncQuery({
    queryKey: () => queryKeys.onboarding.liveTodoCount(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const uid = userId();
      return dbInstance && uid ? getLiveTodoCountForUserQuery(dbInstance, uid) : null;
    },
    enabled: () => Boolean(drizzle()) && isGuest(),
  });
  const listCount = createPowerSyncQuery({
    queryKey: () => queryKeys.onboarding.liveListCount(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const uid = userId();
      return dbInstance && uid ? getLiveListCountForUserQuery(dbInstance, uid) : null;
    },
    enabled: () => Boolean(drizzle()) && isGuest(),
  });

  const hasData = createMemo<boolean | undefined>(() => {
    const todos = todoCount.data?.[0]?.total;
    const lists = listCount.data?.[0]?.total;
    if (typeof todos !== "number" || typeof lists !== "number") {
      return undefined;
    }
    return todos > 0 || lists > 0;
  });

  const visible = createMemo(() =>
    shouldShowOnboarding({ isGuest: isGuest(), hasData: hasData(), marker: marker() }),
  );

  const persistMarker = (value: OnboardingMarker) => {
    const storage = markerStorage();
    if (storage) {
      writeOnboardingMarker(storage, userId(), value);
    }
    setMarker(value);
  };

  const handleSeed = async () => {
    const dbInstance = drizzle();
    const uid = userId();
    if (!dbInstance || !uid || seeding()) {
      return;
    }
    setSeeding(true);
    try {
      const spec = buildWelcomeSeedSpec({
        listName: t(msg`Getting started`),
        completeTask: t(msg`Tap the circle to complete a task`),
        starImportant: t(msg`Star a task to send it to Important`),
        dueDatePlanned: t(msg`Give a task a due date to see it in Planned`),
        myDayPlan: t(msg`Add a task to My Day to plan your day`),
        openDetails: t(msg`Open a task to add notes, subtasks and reminders`),
        detailsNote: t(msg`Notes, subtasks, attachments and reminders live in the task detail.`),
      });
      const result = await applyWelcomeSeed(dbInstance, uid, spec, {
        createList: createListMutation,
        createTodo: createTodoMutation,
      });
      persistMarker("seeded");
      void navigate({ to: "/lists/$listId", params: { listId: result.listId } });
    } catch (error) {
      // The seed failed partway or completely: we do NOT persist the marker so the
      // user can retry (the rows already created make hasData=true and the
      // card hides itself only if the list was created).
      console.warn("[Onboarding] welcome seed failed:", error);
    } finally {
      setSeeding(false);
    }
  };

  return (
    <Show when={visible()}>
      <section
        data-testid="onboarding-card"
        class="rounded-xl border border-accent/20 bg-accent/5 p-5 md:p-6 space-y-4"
        aria-label={t(msg`Welcome`)}
      >
        <div class="flex items-start gap-3">
          <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground">
            <Sparkles class="h-5 w-5" aria-hidden="true" />
          </div>
          <div class="space-y-1">
            <h2 class="text-lg font-semibold tracking-tight">{t(msg`New here? Take a tour`)}</h2>
            <p class="text-sm text-muted-foreground max-w-prose">
              {t(
                msg`Start with a sample list that shows how tasks, Important, Planned and My Day work. You can delete it anytime.`,
              )}
            </p>
          </div>
        </div>
        <div class="flex flex-wrap items-center gap-2 pl-12">
          <Button
            type="button"
            size="sm"
            data-testid="onboarding-seed"
            disabled={seeding()}
            onClick={() => {
              void handleSeed();
            }}
          >
            {t(msg`Create sample list`)}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            data-testid="onboarding-dismiss"
            disabled={seeding()}
            onClick={() => persistMarker("dismissed")}
          >
            {t(msg`Start from scratch`)}
          </Button>
        </div>
      </section>
    </Show>
  );
}
